import React from 'react';
import { XMarkIcon, ClockIcon, StarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import DaySchedule from './DaySchedule';

export default function CourseDetailModal({ course, onClose }) {
  if (!course) return null;

  const formatInstructors = (instructors) => {
    if (!instructors) return 'N/A';
    
    // Handle JSON string format from API
    let parsedInstructors = instructors;
    if (typeof instructors === 'string') {
      try {
        parsedInstructors = JSON.parse(instructors);
      } catch {
        return instructors; // Return as-is if not JSON
      }
    }
    
    if (!Array.isArray(parsedInstructors)) return 'N/A';
    
    return parsedInstructors
      .filter(Boolean)
      .map((instructor) => 
        instructor
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      )
      .join(', ');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-dark-200 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          {/* Header */}
          <div className="bg-white dark:bg-dark-200 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-dark-800">
                  {course.class_name || 'Course Details'}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-dark-600">
                  {course.class_tag} • {course.term}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white dark:bg-dark-200 text-gray-400 hover:text-gray-500 dark:text-dark-600 dark:hover:text-dark-700 focus:outline-none focus:ring-2 focus:ring-accent-primary"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-4 sm:px-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Instructors</h4>
                <p className="text-sm text-gray-600 dark:text-dark-600">
                  {formatInstructors(course.instructors)}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Units</h4>
                <p className="text-sm text-gray-600 dark:text-dark-600">
                  {course.units || 'N/A'} credits
                </p>
              </div>
            </div>

            {/* Schedule */}
            {(course.start_times || course.end_times) && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-3">Schedule</h4>
                <div className="flex justify-center">
                  <DaySchedule
                    startTimes={course.start_times}
                    endTimes={course.end_times}
                  />
                </div>
              </div>
            )}

            {/* Metrics */}
            {(course.mean_hours || course.overall_score_course_mean) && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-3">Course Metrics</h4>
                <div className="flex flex-wrap gap-3">
                  {course.mean_hours && (
                    <div className="flex items-center space-x-2 bg-gray-50 dark:bg-dark-300 px-3 py-2 rounded-lg">
                      <ClockIcon className="h-4 w-4 text-gray-500 dark:text-dark-600" />
                      <span className="text-sm text-gray-700 dark:text-dark-700">
                        {course.mean_hours.toFixed(1)} hours/week
                      </span>
                    </div>
                  )}
                  
                  {course.overall_score_course_mean && (
                    <div className="flex items-center space-x-2 bg-gray-50 dark:bg-dark-300 px-3 py-2 rounded-lg">
                      <StarIcon className="h-4 w-4 text-gray-500 dark:text-dark-600" />
                      <span className="text-sm text-gray-700 dark:text-dark-700">
                        {course.overall_score_course_mean.toFixed(2)}/5.0 rating
                      </span>
                    </div>
                  )}

                  {course.current_enrollment_number && course.enrollment_cap && (
                    <div className="flex items-center space-x-2 bg-gray-50 dark:bg-dark-300 px-3 py-2 rounded-lg">
                      <UserGroupIcon className="h-4 w-4 text-gray-500 dark:text-dark-600" />
                      <span className="text-sm text-gray-700 dark:text-dark-700">
                        {course.current_enrollment_number}/{course.enrollment_cap} enrolled
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {course.description && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Description</h4>
                <p className="text-sm text-gray-600 dark:text-dark-600 leading-relaxed">
                  {course.description}
                </p>
              </div>
            )}

            {/* Additional Details */}
            {(course.course_requirements || course.recommended_prep || course.class_notes) && (
              <div className="space-y-4">
                {course.course_requirements && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Requirements</h4>
                    <p className="text-sm text-gray-600 dark:text-dark-600">
                      {course.course_requirements}
                    </p>
                  </div>
                )}

                {course.recommended_prep && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Recommended Preparation</h4>
                    <p className="text-sm text-gray-600 dark:text-dark-600">
                      {course.recommended_prep}
                    </p>
                  </div>
                )}

                {course.class_notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-dark-800 mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 dark:text-dark-600">
                      {course.class_notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Q-Guide Link */}
            {course.q_report && (
              <div>
                <a
                  href={course.q_report}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-accent-primary hover:bg-accent-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-colors"
                >
                  View Q-Guide Report
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-dark-300 px-4 py-3 sm:px-6 flex justify-end">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}